import React, { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("hispl_token");
    if (!token) {
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then((res) => {
        setUser(res.data);
        localStorage.setItem("hispl_user", JSON.stringify(res.data));
      })
      .catch(() => {
        localStorage.removeItem("hispl_token");
        localStorage.removeItem("hispl_user");
      })
      .finally(() => setLoading(false));
  }, []);

  async function login(email, password) {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("hispl_token", data.token);
    // Fetch full /auth/me so we have the permissions matrix + department.
    const meRes = await api.get("/auth/me");
    localStorage.setItem("hispl_user", JSON.stringify(meRes.data));
    setUser(meRes.data);
    return meRes.data;
  }

  function logout() {
    localStorage.removeItem("hispl_token");
    localStorage.removeItem("hispl_user");
    setUser(null);
    window.location.href = "/login";
  }

  // Permission helper — Admin always allowed; otherwise look up the module/action.
  function hasPerm(module, action = "view") {
    if (!user) return false;
    if (user.role === "admin" || user.role === "super_admin") return true;
    return !!user.permissions?.[module]?.[action];
  }

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, hasPerm }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export const ROLE_LABELS = {
  sales: "Sales & Marketing",
  design_engineer: "Design",
  design_hod: "Design HOD",
  production_supervisor: "Production",
  quality_inspector: "Quality",
  dispatch: "Dispatch",
  plant_head: "Plant Head",
  finance: "Finance",
  purchase: "Purchase & Subcontract",
  admin: "Administrator",
  super_admin: "Super Admin",
};

export function canCreateCylinder(role) {
  return role === "admin" || role === "super_admin";
}

export function canViewCost(role) {
  return ["plant_head", "finance", "admin", "super_admin"].includes(role);
}

export function canViewRealizedCost(role) {
  return ["finance", "admin", "super_admin"].includes(role);
}

export function canHoldOrCancel(role) {
  return ["plant_head", "admin", "super_admin"].includes(role);
}

// Which target statuses a role may set
export const ROLE_STATUS_TARGETS = {
  sales: [],
  design_engineer: ["DESIGN_IN_PROGRESS", "DESIGN_APPROVED"],
  design_hod: [],
  production_supervisor: ["IN_PRODUCTION", "QUALITY_PENDING"],
  quality_inspector: ["QUALITY_PASSED", "QUALITY_FAILED", "REWORK", "REJECTED_SCRAPPED"],
  dispatch: ["READY_FOR_DISPATCH", "DISPATCHED"],
  plant_head: null, // all
  finance: [],
  admin: null,
  super_admin: null,
};
